import { useContext, useMemo, useState } from 'react';
import styles from './SchemaMapView.module.css';
import ThemeContext from 'config/context/themeContext';
import GuiContext from 'config/context/guiContext';
import { useCompositionsContext } from 'features/compositions/context/CompositionsContext';
import { useCompositionAdvancedOptionsContext } from 'features/compositions/context/CompositionAdvancedOptionsContext';
import DatabaseScopesFetcher from 'features/compositions/data-fetchers/DatabaseScopesFetcher';
import { relationOf, descriptionWithoutRelation } from 'features/compositions/utils/relations';
import { displayType, storageNote } from 'features/compositions/utils/fieldTypes';
import { SearchIcon } from 'assets/icons';

// Koliko polja stane na karticu pre „i još N". Kartica je pregled a ne prikaz
// strukture — za ceo spisak postoji View type „structure".
const FIELDS_ON_CARD = 6;

const CompositionCard = ({ composition, compositions, stats, selected, onOpen }) => {
    const rows = stats?.rows;

    // Indeksi idu prvi jer određuju šta se uopšte može filtrirati; ostala polja
    // za njima, redom kojim su i definisana.
    const entries = useMemo(() => {
        const indexNames = new Set((composition.indexes || []).map((i) => i.name));
        return [
            ...(composition.indexes || []).map((f, i) => ({ ...f, badge: i === 0 ? 'PK' : 'IDX' })),
            ...(composition.fields || []).filter((f) => !indexNames.has(f.name)),
        ].map((f) => ({ ...f, relation: relationOf(f, compositions) }));
    }, [composition, compositions]);

    const relationCount = entries.filter((f) => f.relation).length;

    // Veze se NIKAD ne odsecaju. Kartica inače pokazuje prvih `FIELDS_ON_CARD`
    // stavki, a FK kolone kod širih tabela padaju iza toga — na `qatest1` je
    // tako bilo skriveno 7 od 10 strelica, dakle upravo ono zbog čega se mapa
    // i gleda. Ostatak se i dalje sažima u „and N more".
    const head = entries.slice(0, FIELDS_ON_CARD);
    const cutRelations = entries.slice(FIELDS_ON_CARD).filter((f) => f.relation);
    const shown = [...head, ...cutRelations];
    const hidden = entries.length - shown.length;

    return (
        <article
            // Kartica nosi ime kompozicije koju predstavlja. Bez toga se ne
            // razlikuje ni čitaču ekrana ni testu — test je do 28.08.2026. do
            // nje dolazio preko `closest('article')`, dakle penjanjem po
            // čvorovima umesto po onome što se vidi.
            aria-label={composition.name}
            className={`${styles.card} ${selected ? styles.selected : ''}`}
            onClick={() => onOpen(composition.name)}
        >
            <header className={styles.card_head}>
                <button
                    type="button"
                    className={styles.card_title}
                    onClick={(e) => { e.stopPropagation(); onOpen(composition.name); }}
                    title={`Open ${composition.name}`}
                >
                    {composition.name}
                </button>
                <span className={`${styles.rows_badge} ${!rows ? styles.rows_empty : ''}`}>
                    {rows === undefined ? 'empty' : `${rows} ${rows === 1 ? 'row' : 'rows'}`}
                </span>
            </header>

            {composition.description && (
                <div className={styles.card_desc} title={composition.description}>
                    {composition.description}
                </div>
            )}

            <ul className={styles.field_list}>
                {shown.map((f) => {
                    const rel = f.relation;
                    return (
                        <li className={styles.field} key={f.name}>
                            <span className={styles.field_name} title={descriptionWithoutRelation(f.description)}>
                                {f.name}
                            </span>
                            <span className={styles.field_type} title={storageNote(f)}>{displayType(f)}</span>
                            {f.badge && (
                                <span className={f.badge === 'PK' ? styles.pk_badge : styles.idx_badge}>
                                    {f.badge}
                                </span>
                            )}
                            {rel && (rel.composition ? (
                                <button
                                    type="button"
                                    className={styles.rel_link}
                                    onClick={(e) => { e.stopPropagation(); onOpen(rel.composition); }}
                                    title={`References ${rel.table}.${rel.column} — opens ${rel.composition}`}
                                >
                                    → {rel.composition}
                                </button>
                            ) : (
                                // Cilj se nije dao pouzdano razrešiti — ime se
                                // pokazuje, ali bez linka koji bi lagao.
                                <span
                                    className={styles.rel_dead}
                                    title={`References ${rel.table}.${rel.column} in the source; no composition here matches that name`}
                                >
                                    → {rel.table}
                                </span>
                            ))}
                        </li>
                    );
                })}
                {hidden > 0 && <li className={styles.more}>and {hidden} more</li>}
            </ul>

            <footer className={styles.card_foot}>
                <span>{(composition.fields || []).length} fields</span>
                <span>{(composition.indexes || []).length} indexes</span>
                {stats?.scopes > 1 && <span>{stats.scopes} scopes</span>}
                {relationCount > 0 && <span>{relationCount} refs</span>}
            </footer>
        </article>
    );
};

const SchemaMapView = () => {
    const { dark } = useContext(ThemeContext);
    const { selected: { selectedAccount } } = useContext(GuiContext);
    const { databaseStructure, databaseScopes, selectedComposition, selectComposition } = useCompositionsContext();
    const { setViewType } = useCompositionAdvancedOptionsContext();
    const [query, setQuery] = useState('');

    const compositions = useMemo(
        () => databaseStructure?.compositions || [],
        [databaseStructure]
    );

    // Kompozicija bez ijednog reda NEMA scope na lancu — tabela nastaje tek
    // prvim upisom. Odsustvo unosa je dakle podatak („prazna"), ne rupa.
    const stats = useMemo(() => {
        const map = new Map();
        for (const s of databaseScopes || []) {
            if (!s.composition) continue; // db.struct i schemas nisu kompozicije
            const e = map.get(s.composition) || { rows: 0, scopes: 0 };
            e.rows += Number(s.count) || 0;
            e.scopes += 1;
            map.set(s.composition, e);
        }
        return map;
    }, [databaseScopes]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return compositions;
        // Traži se i po imenu kolone — „gde mi je patient_id" je češće pitanje
        // od „kako se zvaše ona tabela".
        return compositions.filter((c) =>
            c.name?.toLowerCase().includes(q) ||
            [...(c.fields || []), ...(c.indexes || [])].some((f) => f.name?.toLowerCase().includes(q))
        );
    }, [compositions, query]);

    const totalRows = useMemo(
        () => compositions.reduce((sum, c) => sum + (stats.get(c.name)?.rows || 0), 0),
        [compositions, stats]
    );

    const open = (name) => {
        selectComposition(name);
        setViewType('browse');
    };

    return (
        <div className={`${styles.schema_map_view} ${dark ? styles.dark : ''}`}>
            <DatabaseScopesFetcher />

            <div className={styles.title_row}>
                <div className={styles.component_title}>
                    Schema map
                    {selectedAccount && <span className={styles.db_name}>{selectedAccount}</span>}
                </div>
                <div className={styles.summary}>
                    {compositions.length} compositions · {totalRows} rows
                </div>
            </div>

            {compositions.length > 0 && (
                <div className={styles.search}>
                    <SearchIcon />
                    <input
                        type="text"
                        value={query}
                        placeholder="Filter by composition or column name"
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </div>
            )}

            <div className={styles.grid_scroll}>
                {compositions.length === 0 ? (
                    <div className={styles.empty}>No compositions in this database yet.</div>
                ) : filtered.length === 0 ? (
                    <div className={styles.empty}>Nothing matches “{query}”.</div>
                ) : (
                    <div className={styles.grid}>
                        {filtered.map((c) => (
                            <CompositionCard
                                key={c.name}
                                composition={c}
                                compositions={compositions}
                                stats={stats.get(c.name)}
                                selected={selectedComposition?.name === c.name}
                                onOpen={open}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SchemaMapView;
